import { SERVICE_ENTRY_ROUTE } from "./serviceEntryRoutes";
import { TenantConfigService } from "../../types/tenantConfig";
import { liveUseCasesWhere } from "../../util/useCases";

/**
 * Which tiles the home screen shows (ADR-0026 §A).
 *
 * Until now one tile per use case, from `config.services`. A municipality that
 * offers sports surfaces, meeting rooms and a barbecue site — all `venue`, all
 * one tenant — needs them booked from separate buttons, because a citizen
 * looking for a room and a citizen looking for a pitch are not on the same
 * errand. So the unit becomes the product category.
 *
 * **The fallback is the point.** A use case with no categories keeps its own
 * single tile, unchanged, which is every tenant today and stays true for every
 * use case a tenant never divides. That is what lets this ship without a
 * migration, without a flag day and without a tenant noticing.
 */

/**
 * The use cases whose booking flow actually SENDS the category to the search.
 *
 * A tile that narrows nothing is the exact failure this feature exists to
 * prevent: two buttons telling the customer these are different things, and
 * then the same list behind both. So a category under a use case that is not
 * listed here keeps its single use-case tile until that flow is wired — the
 * "rooms first" sequencing ADR-0026 §1 already sets.
 *
 * Its members are the use cases with `productCategories` in the description
 * (`util/useCases.ts`), live ones only: the space template, whose apartment and
 * venue flows thread `productCategory`, and since #3429 step 3 the station
 * units, whose location list (`useStationLocationList`) and station list
 * (`fetchGroupsFlow`) send the URL's category. A use case switched on without
 * that is the mistake `__tests__/homeTiles.test.ts` makes visible — it checks
 * each entry's flow forwards the key. The admin-ui counterpart,
 * `CATEGORY_USE_CASES`, is derived the same way; its test pins that this file
 * still is.
 */
export const CATEGORY_AWARE_USE_CASES: readonly string[] = liveUseCasesWhere((d) => d.productCategories);

/**
 * The home tile label of each room use case: its single tile, and its standard
 * category's tile (`homePage.tsx`). The FAQ's offer list reads the same keys
 * (`Faqs.tsx`), so an offer is named as its tile is.
 */
export const ROOM_TILE_LABEL_KEYS: Readonly<Record<string, string>> = {
  apartment: "home.room_rental",
  venue: "home.venue_reservation",
  hall: "home.hall_reservation",
};

/**
 * The same for the station units (#3429 step 3): the words their single tile
 * carries (`homePage.tsx`), so a tenant who adds a category sees the standard
 * category's tile keep the use case's name. Storage's tenant-specific wording
 * on its single tile is not repeated here: that tenant runs on its own store,
 * which serves no categories.
 */
export const STATION_TILE_LABEL_KEYS: Readonly<Record<string, string>> = {
  rental: "home.product_rental",
  storage: "home.temporary_storage",
  sup: "home.product_sup",
};

/**
 * The link key of a use case's STANDARD product category (ADR-0026 §W): every
 * product without a category of its own. It has no `productcategory` row and
 * so no real key; this sentinel cannot collide with one, because a real key
 * must start with a letter.
 *
 * MIRROR: `STANDARD_CATEGORY_KEY` in `backend/src/core/lib/categoryKeyResolution.ts`,
 * which narrows the search to exactly those products. Changed together.
 *
 * The category-config readers need no case for it: no category carries this
 * key, so every reader falls back to the use case — which IS the standard
 * category's configuration.
 */
export const STANDARD_CATEGORY_KEY = "_standard";

/**
 * Is this the use case's STANDARD product category — the real row every
 * product without a further category belongs to (ADR-0026 §AE)? Identified by
 * its reserved key `standard_<useCase>`.
 *
 * MIRROR of `isStandardCategory` in
 * `new-backend/admin/src/lib/catalogue/standardCategory.ts`, the enforcing
 * copy. Changed together.
 */
export const isStandardCategoryKey = (category: Pick<PublicCategory, "key" | "useCase">): boolean =>
    category.key === `standard_${category.useCase}`;

/**
 * Is the standard category's own tile held back for this use case? It is while
 * uncategorised products remain — the `_standard` tile lists them and the
 * row's products, and opens date first — and while that is unknown (ADR-0026
 * §AI). The FAQ asks the same question (`faqFlows.ts`), so it never describes
 * an entry order the home screen does not offer.
 */
export const holdsBackStandardRow = (
    standardCatalogue: readonly string[] | undefined,
    service: string,
): boolean => !standardCatalogue || standardCatalogue.includes(service);

/**
 * The German name each use case's STANDARD category is born with (ADR-0026
 * §AE) — the words its home tile carries anyway (`home.*`). A standard row
 * still under this name has not been renamed, so its tile keeps the use case's
 * own translation; once the tenant renames it, the tile shows the new name.
 *
 * MIRROR: `STANDARD_CATEGORY_NAMES` (`.de`) in
 * `new-backend/admin/src/lib/catalogue/standardCategory.ts`, which writes the
 * name; `__tests__/homeTiles.test.ts` pins the two together.
 */
export const STANDARD_CATEGORY_DEFAULT_NAMES: Readonly<Record<string, string>> = {
  venue: "Veranstaltungsräume",
  apartment: "Gästewohnungen",
  hall: "Sporthallen",
  rental: "Produktmiete",
  storage: "Aufbewahrung",
  p2p: "Übergabe",
  parcel: "Paketlieferung",
  sup: "SUP",
  keys: "Schlüsselübergabe",
};

export interface PublicCategory {
    key: string;
    useCase: string;
    name: string;
    langNames: Record<string, { title?: string }> | null;
    /**
     * Does this category hold at least one bookable product?
     *
     * **Absent means "unknown", and is read as `true`.** It is absent from an
     * older deployment of the app backend, and whenever that service could not
     * establish the answer. Reading absent as `false` would blank every
     * category tile a tenant has the moment the two sides are a deploy apart —
     * strictly worse than the tile this flag exists to suppress.
     */
    hasBookableProducts?: boolean;
}

/**
 * Would this category's tile open a catalogue that can never match anything?
 *
 * A category with no products assigned is the case: its tile REPLACES the use
 * case's working tile, so creating an empty category takes a tenant's booking
 * offline without anyone being told — a tile appeared, nothing disappeared,
 * and the list behind it is simply empty.
 *
 * Deliberately NOT a check on whether results exist today. Dates, guests and
 * capacity empty a catalogue all the time and that is an availability answer,
 * not a broken tile. Only the structural case is suppressed.
 */
const hasCatalogue = (category: PublicCategory): boolean =>
    category.hasBookableProducts !== false;

export interface HomeTile {
    /** Stable React key; also what distinguishes two tiles of one use case. */
    id: string;
    to: string;
    /** Drives the icon and the stored "selected service" — still the use case. */
    useCase: TenantConfigService;
    /**
     * The category's label, or null when this tile stands for the whole use
     * case and the screen should use its existing translation. Null is not
     * "missing" — it is the instruction to fall back, and keeps the wording of
     * every tenant's current tiles byte-for-byte what it is now.
     */
    label: string | null;
    /**
     * True for the tile of the use case's STANDARD product category, shown
     * beside its category tiles (ADR-0026 §W). It carries `label: null` like a
     * whole-use-case tile — the screen names it with the use case's own
     * translation — unless the tenant renamed the standard category, whose
     * name it then carries (`standardCategoryTileLabel`); it leads to the
     * uncategorised products only.
     */
    standard?: boolean;
}

/**
 * The label a category shows, or null to fall back to the use case's own.
 *
 * `langNames[lang].title` first, then the stored `name`. A tenant types one
 * name and may add an English title later; an absent translation must fall
 * back to something real rather than render a key or an empty tile.
 */
export const categoryLabel = (
    // Only the two fields it reads, so the archived-category notice on the
    // search step (`api/apartment/categoryOutcome.ts`) resolves its name by
    // this same rule rather than a copy of it.
    category: Pick<PublicCategory, "name" | "langNames">,
    language: string,
): string => {
    const title = category.langNames?.[language.toLowerCase().slice(0, 2)]?.title;
    return (typeof title === "string" && title.trim().length > 0)
        ? title.trim()
        : category.name;
};

/**
 * The label of a use case's STANDARD category tile, or null to keep the use
 * case's own translation (ADR-0026 §AE): its name once the tenant has renamed
 * it, null while it carries the name it was born with — or when the tenant's
 * categories do not include it.
 *
 * Read from the row whether or not the row has a catalogue of its own: while
 * uncategorised products remain, the `_standard` tile — and, with no other
 * category to show, the use case's single tile — stand for this row, so they
 * carry its name. Before this, a tenant that renamed its "Sporthallen" standard
 * category still saw "Sporthallen" on the home screen.
 */
export const standardCategoryTileLabel = (
    categories: readonly PublicCategory[],
    useCase: string,
    language: string,
): string | null => {
    const row = categories.find((category) => category.useCase === useCase && isStandardCategoryKey(category));
    if (!row) return null;
    const bornWith = STANDARD_CATEGORY_DEFAULT_NAMES[useCase];
    if (bornWith !== undefined && row.name.trim() === bornWith) return null;
    return categoryLabel(row, language);
};

/** Where a category's tile leads: its use case's entry, narrowed by key. */
export const categoryEntryRoute = (category: PublicCategory): string => {
    const base = SERVICE_ENTRY_ROUTE[category.useCase as TenantConfigService];
    // No route for this use case — the tile would lead nowhere, so it is not
    // offered. Returning "" lets the caller drop it rather than render a dead
    // card, which is what a `to=undefined` Link would become.
    if (!base) return "";
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}category=${encodeURIComponent(category.key)}`;
};

/**
 * @param services   the tenant's home-screen entries, in their stored order
 *                   (may include the `moreToCome` pseudo-service)
 * @param categories the tenant's categories; empty for every tenant today
 * @param language   the active UI language, for the label
 */
export const homeTiles = (
    services: string[],
    categories: PublicCategory[],
    language: string,
    /**
     * Use cases whose standard product category still holds a catalogue
     * (ADR-0026 §W). Absent = not established, and then nothing is added:
     * today's tiles stay exactly as they are.
     */
    standardCatalogue?: string[],
): HomeTile[] => {
    const byUseCase = new Map<string, PublicCategory[]>();
    // Categories with no catalogue behind them are dropped HERE, before the
    // grouping, and that placement is the fix: a use case whose only category
    // is empty then has no categories at all, and falls through to the
    // unchanged single-tile path below. Filtering later — inside the loop,
    // after `own.length === 0` had already been answered — would suppress the
    // dead tile and leave the use case with NO tile, which is the same outage
    // wearing different clothes.
    categories.filter(hasCatalogue).forEach((category) => {
        const list = byUseCase.get(category.useCase) ?? [];
        list.push(category);
        byUseCase.set(category.useCase, list);
    });

    const tiles: HomeTile[] = [];
    services.forEach((service) => {
        // The standard row's own tile is held back while uncategorised
        // products remain — the sentinel tile below lists them AND the row's
        // products (`availabilityFilters.ts`) — and also while that could not
        // be established: an unknown answer must not narrow the use case's
        // tile to the row's products alone (ADR-0026 §AI).
        const holdBackStandardRow = holdsBackStandardRow(standardCatalogue, service);
        const own = (CATEGORY_AWARE_USE_CASES.includes(service)
            ? byUseCase.get(service) ?? []
            : []
        ).filter((category) => !(holdBackStandardRow && isStandardCategoryKey(category)));
        // A renamed standard category names the tile that stands for it.
        const standardLabel = CATEGORY_AWARE_USE_CASES.includes(service)
            ? standardCategoryTileLabel(categories, service, language)
            : null;
        if (own.length === 0) {
            // The unchanged path: one tile, today's route — and the use case's
            // own translation, unless the tenant renamed its standard category.
            tiles.push({
                id: service,
                to: SERVICE_ENTRY_ROUTE[service as TenantConfigService] ?? "",
                useCase: service as TenantConfigService,
                label: standardLabel,
            });
            return;
        }
        // Once a use case shows category tiles, its own tile is gone — and
        // with it every product NOT filed under a category. While those still
        // exist, the standard category keeps a tile of its own, first, under
        // the use case's name.
        const base = SERVICE_ENTRY_ROUTE[service as TenantConfigService];
        if (base && standardCatalogue?.includes(service)) {
            const separator = base.includes("?") ? "&" : "?";
            tiles.push({
                id: `${service}:${STANDARD_CATEGORY_KEY}`,
                to: `${base}${separator}category=${STANDARD_CATEGORY_KEY}`,
                useCase: service as TenantConfigService,
                label: standardLabel,
                standard: true,
            });
        }
        // The standard category's tile first, as it is first in the admin;
        // the others keep their served order.
        const ordered = [
            ...own.filter(isStandardCategoryKey),
            ...own.filter((category) => !isStandardCategoryKey(category)),
        ];
        ordered.forEach((category) => {
            const to = categoryEntryRoute(category);
            if (!to) return;
            tiles.push({
                id: `${service}:${category.key}`,
                to,
                useCase: service as TenantConfigService,
                label: categoryLabel(category, language),
            });
        });
    });
    return tiles;
};

/**
 * The category key a booking flow was entered with, from the URL.
 *
 * Read from the query string rather than held in Redux for the same reason
 * every other entry parameter is (city, district, dates): a refresh or a
 * shared link must land on the same catalogue, and a store that starts empty
 * would silently widen the search back to the whole use case.
 *
 * Validated against the key's own shape before use. The value is
 * attacker-controllable — it is a URL parameter — and it reaches a SQL
 * parameter, so anything that is not a well-formed key is treated as absent
 * rather than passed on.
 */
/**
 * Is this a well-formed `productcategory.key`?
 *
 * THE single definition of the shape, because it is applied in two places that
 * must not disagree: the entry URL (below) and the booking flow's own query
 * schema, which carries the category across every step
 * (`bookLocation/common/url/`). A second copy of the pattern would eventually
 * accept at one end what the other rejects, and the symptom would be a
 * category that works on the list and vanishes at the summary.
 */
export const isValidCategoryKey = (value: unknown): boolean =>
    typeof value === "string" && /^[a-z][a-z0-9_]{1,31}$/.test(value);

/**
 * The key as the database stores it — lower-cased, nothing else (PO,
 * 2026-09-28).
 *
 * A key is lower-case by construction: `productcategory.key` carries a CHECK
 * of exactly the pattern above, so folding the case can only ever make a value
 * match a category that really exists — it cannot invent a match. Without it a
 * hand-typed or auto-capitalised `Sporthalle` was discarded as malformed, and
 * the customer who followed that link silently got the full list instead of
 * their category. Case-insensitive comparison of API values is the house rule
 * everywhere else; this is that rule applied to the one value the customer can
 * type themselves.
 *
 * Deliberately not `trim()` or any other repair: folding case is a notation
 * difference for the same key, while whitespace or punctuation means the value
 * did not come from one of our links, and those still fall through to
 * `hasUnusableCategory`.
 */
const normalizeCategoryKey = (raw: string): string => raw.toLowerCase();

/**
 * A raw URL value as a usable key, or `undefined` — normalise, then validate.
 *
 * Exported because the booking flow's query parser reads the same parameter and
 * must reach the same verdict AND the same string. Handing it the predicate
 * alone was not enough once normalisation existed: it validated the raw value,
 * so `?category=Sporthalle` resolved on the home screen and vanished at step 1.
 */
export const categoryKeyOf = (raw: unknown): string | undefined => {
    if (typeof raw !== "string" || !raw) return undefined;
    const key = normalizeCategoryKey(raw);
    // The standard category's link (ADR-0026 §W) — a sentinel, so it fails
    // the key shape on purpose and is admitted by name.
    if (key === STANDARD_CATEGORY_KEY) return key;
    return isValidCategoryKey(key) ? key : undefined;
};

export const categoryFromSearch = (search: string): string | undefined =>
    categoryKeyOf(new URLSearchParams(search).get("category"));

/**
 * Did this URL ASK for a category that cannot be one — an injection string, or
 * tampering? Such a value never reaches the server (see above), so the server
 * cannot report it as unknown; this is the client's half of that answer
 * (#2764 F9). Returns only a boolean: the value itself is customer-controlled
 * and is never kept or rendered.
 *
 * A merely mis-capitalised key is NOT unusable — it is normalised above and
 * resolved like any other, so it must not land on the "this cannot be a
 * category" notice.
 */
export const hasUnusableCategory = (search: string): boolean => {
    const raw = new URLSearchParams(search).get("category");
    return !!raw && categoryKeyOf(raw) === undefined;
};
